import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { GALLERY, GALLERY_CATS } from "../lib/images";
import { Overline, Reveal, EASE } from "./Reveal";

const spanFor = (i) => (i === 0 ? "col-span-2 row-span-2" : i === 4 || i === 8 || i === 12 ? "col-span-2" : "");

export default function Gallery() {
  const { t, lang } = useI18n();
  const [filter, setFilter] = useState("todas");
  const [lightbox, setLightbox] = useState(null);

  const items = filter === "todas" ? GALLERY : GALLERY.filter((g) => g.cat === filter);

  return (
    <section id="galeria" data-testid="gallery-section" className="grain bg-charcoal py-24 text-cream md:py-32">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="max-w-xl">
            <Reveal>
              <Overline light>{t.galeria.overline}</Overline>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 font-serif text-3xl leading-tight sm:text-4xl lg:text-5xl">
                {t.galeria.title}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.14}>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {["todas", ...GALLERY_CATS].map((cat) => (
                <button
                  key={cat}
                  data-testid={`gallery-filter-${cat}`}
                  onClick={() => setFilter(cat)}
                  className={`pb-1 text-[12px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                    filter === cat
                      ? "border-b border-terra text-terra"
                      : "text-cream/50 hover:text-cream"
                  }`}
                >
                  {t.galeria.filters[cat]}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <motion.div layout className="mt-14 grid grid-flow-dense grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.button
                layout
                key={item.url}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: EASE }}
                data-testid={`gallery-item-${i}`}
                onClick={() => setLightbox(item)}
                className={`group relative overflow-hidden bg-ember ${spanFor(i)}`}
              >
                <img
                  src={item.url}
                  alt={item.cap[lang]}
                  loading="lazy"
                  className="aspect-[4/3] h-full w-full object-cover transition-transform duration-[1100ms] ease-out group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-charcoal/0 transition-colors duration-500 group-hover:bg-charcoal/35" />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-3 bg-gradient-to-t from-charcoal/80 to-transparent p-4 pt-8 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="font-corm text-lg italic text-cream">{item.cap[lang]}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex flex-col items-center justify-center bg-charcoal/95 p-5 backdrop-blur-sm"
            onClick={() => setLightbox(null)}
          >
            <motion.img
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              src={lightbox.url}
              alt={lightbox.cap[lang]}
              className="max-h-[78vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <p className="mt-5 font-corm text-xl italic text-cream/85">{lightbox.cap[lang]}</p>
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setLightbox(null)}
              className="absolute right-5 top-5 rounded-full border border-cream/30 p-2.5 text-cream transition-colors hover:bg-cream/10"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
